// import { connect } from 'getstream';
import { useCommunityStore } from '@/stores';
import { StreamChat } from 'stream-chat';

const CHAT_SCRIPTS = [
  { src: 'https://cdn.jsdelivr.net/npm/react@17.0.2/umd/react.production.min.js', global: 'React' },
  { src: 'https://cdn.jsdelivr.net/npm/react-dom@17/umd/react-dom.production.min.js', global: 'ReactDOM' },
  { src: 'https://cdn.jsdelivr.net/npm/stream-chat@8.14.3', global: 'StreamChat' },
  { src: 'https://cdn.jsdelivr.net/npm/stream-chat-react@10.20.1', global: 'StreamChatReact' },
];

function loadScript(src: string, globalName: string, retries = 2): Promise<any> {
  const win = window as any;
  if (win[globalName]) {
    return Promise.resolve(win[globalName]);
  }

  return new Promise((resolve, reject) => {
    // Check if script tag already exists (e.g. from useHead on full page load)
    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    if (existing) {
      // Script tag exists but global isn't set yet — wait for it
      const startTime = Date.now();
      const interval = setInterval(() => {
        if (win[globalName]) {
          clearInterval(interval);
          console.log(`${globalName} loaded`);
          resolve(win[globalName]);
        } else if (Date.now() - startTime > 30000) {
          clearInterval(interval);
          // Remove the failed script tag so a retry can re-add it
          existing.remove();
          reject(new Error(`${globalName} failed to load within 30s`));
        }
      }, 200);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.onload = () => {
      console.log(`${globalName} loaded`);
      resolve(win[globalName]);
    };
    script.onerror = () => {
      script.remove();
      reject(new Error(`Failed to load script: ${src}`));
    };
    document.head.appendChild(script);
  }).catch((error) => {
    if (retries > 0) {
      console.warn(`${globalName} load failed, retrying... (${retries} left)`);
      return loadScript(src, globalName, retries - 1);
    }
    throw error;
  });
}

let chatScriptsPromise: Promise<void> | null = null;

/**
 * Loads all chat-related CDN scripts in order, guaranteeing each
 * dependency is fully loaded before the next script executes.
 * Safe to call multiple times — only loads once.
 * Resets on failure so retries (e.g. "Try to reload chat") start fresh.
 */
export function loadChatScripts(): Promise<void> {
  if (!chatScriptsPromise) {
    chatScriptsPromise = (async () => {
      for (const { src, global: globalName } of CHAT_SCRIPTS) {
        await loadScript(src, globalName);
      }
    })();
    chatScriptsPromise.catch(() => {
      chatScriptsPromise = null;
    });
  }
  return chatScriptsPromise;
}

export async function streamChatLoaded() {
  await loadChatScripts();
  return (window as any).StreamChat;
}

export async function reactDOMLoaded() {
  await loadChatScripts();
  return (window as any).ReactDOM;
}

export async function reactLoaded() {
  await loadChatScripts();
  return (window as any).React;
}

export async function StreamChatReactLoaded() {
  await loadChatScripts();
  return (window as any).StreamChatReact;
}

export async function connectChatUser(id, name, image, chatToken) {
  const chatClient = StreamChat.getInstance(
    // @ts-ignore
    process.env.VUE_APP_STREAM_CHAT_API_KEY
  );
  useCommunityStore().disconnectChatClient()
  useCommunityStore().communityChatClient({ chatClient })

  const member = await chatClient.connectUser({ id, name, image }, chatToken);
  // @ts-ignore
  useCommunityStore().setNumberOfChatNotifications(member.me.unread_channels)

  chatClient.on(event => {
    // console.log(event)
    // if (event.total_unread_count !== undefined) {
    //   console.log(event.total_unread_count);
    // }
    if (event.unread_channels !== undefined) {
      useCommunityStore().setNumberOfChatNotifications(event.unread_channels)
    }
  });
}

export async function subscribeStudioToCommunityNotifications(
  token,
  communityProfileId,
  cb = data => {}
) {
  console.log('subscribeStudioToCommunityNotifications -> token:', token);
  console.log(
    'subscribeStudioToCommunityNotifications -> communityProfileId:',
    communityProfileId
  );
  // @ts-ignore
  const apiKey: string = process.env.VUE_APP_STREAM_CHAT_API_KEY;
  // @ts-ignore
  const appId: string = process.env.VUE_APP_STREAM_APP_ID;
  // console.log('stream connection apiKey = ', apiKey);
  // console.log('stream connection appId = ', appId);
  const { connect } = await import('getstream');
  const client = connect(apiKey, null, appId);
  const notificationFeed = client.feed(
    'notification',
    communityProfileId,
    token
  );

  function successCallback() {
    console.log('Studio community notifications listener successfuly set up');
  }

  function failCallback(data) {
    console.log(
      'Failed to set up studio community notifications listener: ',
      data
    );
  }

  notificationFeed.subscribe(cb).then(successCallback, failCallback);
}

export async function subscribeToCommunityNotifications(
  token,
  communityProfileId,
  cb = data => {}
) {
  console.log('subscribeToCommunityNotifications -> token:', token);
  console.log(
    'subscribeToCommunityNotifications -> communityProfileId:',
    communityProfileId
  );
  // @ts-ignore
  const apiKey: string = process.env.VUE_APP_STREAM_CHAT_API_KEY;
  // @ts-ignore
  const appId: string = process.env.VUE_APP_STREAM_APP_ID;
  // console.log('stream connection apiKey = ', apiKey);
  // console.log('stream connection appId = ', appId);
  const { connect } = await import('getstream');
  const client = connect(apiKey, null, appId);
  const notificationFeed = client.feed(
    'notification',
    communityProfileId,
    token
  );

  function successCallback() {
    console.log(
      'Community notifications listener successfuly set up for ' +
        communityProfileId
    );
  }

  function failCallback(data) {
    console.log(
      'Failed to set up studio community notifications listener for ' +
        communityProfileId +
        ': ',
      data
    );
  }

  notificationFeed.subscribe(cb).then(successCallback, failCallback);
}
